import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { useAuthSession } from '@/features/auth/useAuthSession';
import {
  fetchAdminPacks,
  fetchAdminPayments,
  fetchAdminRevenue,
  refundAdminPayment,
  saveAdminPack,
  type AdminPackInput,
} from '@/services/admin';

/**
 * Платёжные разделы админки. Отдельный хук, чтобы не раздувать
 * useAdminConsole; ничего не считается на клиенте — права и значения
 * проверяет функция `admin`.
 */
export function usePaymentsAdmin() {
  const { status, session } = useAuthSession();
  const queryClient = useQueryClient();
  const [days, setDays] = useState<7 | 30>(30);

  const token = session?.access_token ?? '';
  const enabled = status === 'ready' && Boolean(session);

  const packs = useQuery({ queryKey: ['admin', 'packs'], queryFn: () => fetchAdminPacks(token), enabled, retry: false });
  const payments = useQuery({ queryKey: ['admin', 'payments'], queryFn: () => fetchAdminPayments(token), enabled, retry: false });
  const revenue = useQuery({ queryKey: ['admin', 'revenue', days], queryFn: () => fetchAdminRevenue(days, token), enabled, retry: false });

  const savePack = useMutation({
    mutationFn: (pack: AdminPackInput) => saveAdminPack(pack, token),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'packs'] });
      // Витрина у людей тоже изменилась.
      void queryClient.invalidateQueries({ queryKey: ['credit-packs'] });
    },
  });

  const refund = useMutation({
    mutationFn: (paymentId: string) => refundAdminPayment(paymentId, token),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'payments'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'revenue'] });
    },
  });

  return { packs, payments, revenue, days, setDays, savePack, refund };
}
